import { apiClient } from './client';
import type { ReportStatus } from './reports';

/**
 * Longitudinal evaluation (LONGITUDINAL-ANALYTICS): one participant × one
 * procedure, each configured metric mapped across their sessions in time order
 * with a per-metric trend assessment. Mirrors the scorecard's metric registry,
 * good-direction, and reference cohorts.
 */

export interface LongitudinalReference {
  cohort: string;
  tier: number;
  value: number;
  statistic: string;
  sample_size: number | null;
  source: string | null;
  significant: boolean;
}

export interface LongitudinalPoint {
  session_id: string;
  started_at: string;
  value: number;
}

/**
 * Least-squares trend line through every session, value against session
 * number 1…n (LONGITUDINAL-TREND). Present from 3 sessions on.
 */
export interface LongitudinalTrend {
  n: number;
  /** Change per session, in the metric's unit. */
  slope: number;
  slope_ci_low: number;
  slope_ci_high: number;
  r_squared: number;
  /** Two-sided p-value of the slope (H0: no trend). */
  p_value: number;
  /** p < 0.05 */
  significant: boolean;
  residual_se: number;
  /** The line and its 95 % confidence band, one value per session. */
  fitted: number[];
  band_low: number[];
  band_high: number[];
}

export interface LongitudinalMetric {
  key: string;
  modality: string;
  unit: string | null;
  direction: 'higher' | 'lower' | 'none';
  points: LongitudinalPoint[];
  n: number;
  first: number | null;
  latest: number | null;
  /**
   * Direction-aware first→latest improvement, signed (+ = better) — along the
   * trend line from 3 sessions on.
   */
  change_pct: number | null;
  improved: boolean | null;
  /** From 3 sessions on, improving/declining needs a significant slope. */
  trend: 'improving' | 'declining' | 'flat' | null;
  trend_fit?: LongitudinalTrend | null;
  tier: 'elite' | 'great' | 'good' | 'bad' | null;
  references: LongitudinalReference[];
  /** True when the read is soft (few sessions / relative-only / no ref). */
  indicative: boolean;
}

export interface LongitudinalReport {
  participant_user_id: string;
  participant_label: string | null;
  procedure_id: string;
  procedure_name: string | null;
  session_count: number;
  metrics: LongitudinalMetric[];
}

export async function getParticipantProcedureLongitudinal(
  tenantId: string,
  participantUserId: string,
  procedureId: string,
): Promise<LongitudinalReport> {
  const { data } = await apiClient.get<LongitudinalReport>(
    `/api/v1/tenants/${tenantId}/participants/${participantUserId}` +
      `/procedures/${procedureId}/longitudinal`,
  );
  return data;
}

/** Progression for the participant + procedure OF a session — what the
 *  scorecard's "Longitudinal reports" menu opens for the row it's showing. */
export async function getSessionLongitudinal(
  tenantId: string,
  sessionId: string,
): Promise<LongitudinalReport> {
  const { data } = await apiClient.get<LongitudinalReport>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/longitudinal`,
  );
  return data;
}

/** Enqueue a longitudinal PDF render for a session's participant × procedure
 *  (idempotent while one is in flight). */
export async function requestSessionLongitudinalReport(
  tenantId: string,
  sessionId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/longitudinal-report`,
  );
}

/** Longitudinal report status + (when ready) a presigned download URL. */
export async function getSessionLongitudinalReport(
  tenantId: string,
  sessionId: string,
): Promise<ReportStatus> {
  const { data } = await apiClient.get<ReportStatus>(
    `/api/v1/tenants/${tenantId}/sessions/${sessionId}/longitudinal-report`,
  );
  return data;
}

// --- Participant self-service (their OWN session) --------------------------

/** Progression for the participant's own session — the /me analogue of
 *  `getSessionLongitudinal` (resolves the owning tenant server-side). */
export async function getMySessionLongitudinal(
  sessionId: string,
): Promise<LongitudinalReport> {
  const { data } = await apiClient.get<LongitudinalReport>(
    `/api/v1/sessions/me/${encodeURIComponent(sessionId)}/longitudinal`,
  );
  return data;
}

/** Enqueue a longitudinal PDF render for the participant's own session. */
export async function requestMySessionLongitudinalReport(
  sessionId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/sessions/me/${encodeURIComponent(sessionId)}/longitudinal-report`,
  );
}

/** Longitudinal report status + download URL for the participant's own
 *  session. */
export async function getMySessionLongitudinalReport(
  sessionId: string,
): Promise<ReportStatus> {
  const { data } = await apiClient.get<ReportStatus>(
    `/api/v1/sessions/me/${encodeURIComponent(sessionId)}/longitudinal-report`,
  );
  return data;
}
